import { useNavigate, useSearch } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { AuthShell } from '@/components/auth/auth-shell';
import { SocialButtons } from '@/components/auth/social-buttons';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';
import {
  type AuthPageSearch,
  authorizeReplayUrl,
  consentPageUrl,
  hardNavigate,
  socialErrorMessage,
  withCurrentSearch,
} from '@/lib/oauth';

export function SignupPage() {
  const navigate = useNavigate();
  // Grant params arrive in two shapes (see login.tsx); signUp.email
  // auto-signs-in, so the same resume branches apply. `error` is appended by
  // better-auth when a social sign-in is cancelled/fails at the provider.
  const search = useSearch({ strict: false }) as AuthPageSearch;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(() => socialErrorMessage(search.error));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const { error: signUpError } = await authClient.signUp.email({
        name,
        email,
        password,
        // Workspace name defaults to user name (server auto-creates organization on signup).
        callbackURL: '/onboarding',
      });
      if (signUpError) {
        setError(signUpError.message ?? 'Sign up failed. Please try again.');
      } else if (search.client_id && search.redirect_uri) {
        // Authorize bounce — replay the original authorize request verbatim;
        // the fresh session lets it proceed to the consent screen.
        hardNavigate(authorizeReplayUrl());
      } else if (search.code && search.client_id) {
        // Consent-screen bounce — back to consent with the fresh session.
        hardNavigate(consentPageUrl(search.code, search.client_id));
      } else if (search.invitation) {
        // Invitation detour — back to the accept page rather than /onboarding:
        // an invitee is joining a workspace that is already set up, and signup
        // deliberately created no workspace for them to onboard into
        // (ensureWorkspaceForUser defers while an invitation is pending).
        await navigate({
          to: '/accept-invitation/$invitationId',
          params: { invitationId: search.invitation },
        });
      } else {
        await navigate({ to: '/onboarding' });
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <AuthShell>
        <div className="mb-6">
          <h1 className="text-lg font-semibold">Create your account</h1>
          <p className="mt-1 text-sm text-faint">
            Get started — your workspace is created automatically.
          </p>
        </div>
        <SocialButtons search={search} onError={setError} />
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="space-y-4"
        >
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="name">Your name</Label>
            <Input
              id="name"
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              placeholder="Alex Smith"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              placeholder="you@example.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError(null);
              }}
              placeholder="At least 8 characters"
            />
          </div>
          <Button type="submit" size="cta" className="w-full" disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-faint">
          Already have an account?{' '}
          {/* Plain anchor: carries an in-flight OAuth grant query verbatim
              without router search-param typing at every Link call site. */}
          <a
            href={withCurrentSearch('/login')}
            className="font-semibold text-foreground hover:underline"
          >
            Sign in
          </a>
        </p>
      </AuthShell>
    </div>
  );
}
